import {
  ChatMessageSearchType,
  ChatMessageType,
  ChatSettingLog,
  ChattingOptions,
  Schema$ChatMessage,
  SimpleChatUserInfo,
} from 'adv-lib/src/v2/interface/chat'
import { schema_chat_message } from 'adv-lib/src/models/chat/chat-message/chat-message'
import { schema_user_chat_property } from 'adv-lib/src/models/chat/user-chat-property'
import { schema_chat_forbid_word } from 'adv-lib/src/models/chat/chat-forbid-word'
import { schema_chat_emoticon } from 'adv-lib/src/models/chat/chat-emoticon'
 import { Schema$ResponseBase } from 'adv-lib/src/v2/interface/common'
import { GAME_STATUS, MATCH_GAME_STATUS, MATCH_STATUS } from 'adv-protocol/src/GameCommon'
import { schema_cheer_for_team } from 'adv-lib/src/models/dedicated/cheer/cheer-for-team'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from 'adv-lib/src/v2/constants/cheer'

export interface CommonPagination {
  pageNum: number
  pageLimit: number
  sortBy?: string
  sortDirection?: string // desc, asc
}

export interface FindChatMessageListOptions extends CommonPagination {
  fromDate: Date
  toDate: Date
  chatType?: ChatMessageType
  searchType?: ChatMessageSearchType
  searchWord?: string
}

export interface FindChatMessageListResponse {
  chatMessageList: Partial<schema_chat_message>[]
  chatMessageCount: number
  cheerForTeamList: Partial<schema_cheer_for_team[]>
}

export enum CHEER_FOR_TEAM_MATCH_TYPE {
  MY_BET, FAVORITE, LIVE,
}

export enum CHEER_FOR_TEAM_MATCH_SUB_TYPE {
  ONGOING, NOT_STARTED, CLOSED,
}

export interface Schema$CheerForTeamData {
  id?: number
  matchId: number
  dateOfMatch: Date
  sportId: number
  categoryId: number
  tournamentId: number
  matchStatus: MATCH_STATUS
  matchStatusName: string
  status: MATCH_GAME_STATUS
  statusName: string
  homeId: number
  homeName: string
  homeEmblem: string
  homeScore: number
  homePeriodScores: number[]
  awayId: number
  awayName: string
  awayEmblem: string
  awayScore: number
  awayPeriodScores: number[]
  isFavoriteMatch: boolean
  effectIconUrl: string
  effectEnabled?: boolean
  contentsType?: CHEER_FOR_TEAM_CONTENTS_TYPE,
  attachment?: string
  attachmentDetail?: string
  matchDetail?: string
  contents?: string
  createdAt?: Date
}

export namespace IChatMessageRoute {
  export interface Params extends FindChatMessageListOptions {
  }

  export interface Response extends FindChatMessageListResponse {
  }
}

export namespace IChatOptionsRoute {
  export interface Params extends ChattingOptions {
  }

  export interface Response extends ChattingOptions {
  }
}

export namespace IFindUserChatPropertyRoute {
  export interface Params {
    userId: string
  }

  export interface Response extends Partial<schema_user_chat_property> {
  }
}

export namespace IUserChatPropertyRoute {
  export interface Params extends Partial<schema_user_chat_property> {
    chatId: number // chat message id (schema_user_chat_property 에 이미 id 가 존재해서...)
    chattingId: string // uuid
    targetUserId: string
    targetUserNick: string
    noChattingTimeTypeText?: string
  }

  export interface Response {
    result: boolean
  }
}

export namespace IUpdateChatMessageRoute {
  export interface Params {
    chatMessage: Partial<schema_chat_message>
    updateType: string
    updateReasonId?: string
    updateReasonText?: string
    customReasonText?: string
  }

  export interface Response extends Partial<schema_chat_message> {
  }
}

export namespace IFindChatSettingLog {
  export interface Params {
    fromDate: Date
    pageNum: number
    pageLimit: number
    logType?: number
  }

  export interface Response {
    chatSettingLogList: ChatSettingLog[]
    totalCount: number
  }
}

export namespace IRemoveUserProfileRoute {
  export interface Params {
    userId: string
    nick: string
    updateReasonId: string
    updateReasonText: string
    customReasonText?: string
  }

  export interface Response {
  }
}

export namespace IFindChatMessage {
  export interface Params {
    id?: number // chatMessage.id
    chatId: string // chatMessage.chatId UUID
  }

  export interface Response {
  }
}

export namespace IFindChatBlockFavoriteUserRoute {
  export interface Params {
    blockUserOnly: boolean
  }

  export interface Response {
    blockUserList: SimpleChatUserInfo[]
    favoriteUserList: SimpleChatUserInfo[]
  }
}

export namespace IFindWithPagination {
  export interface Params extends CommonPagination {
    searchWord?: string
  }

  export interface Response {
    forbidList: schema_chat_forbid_word[]
    totalCount: number
  }
}

export namespace IAddForbidWord {
  export interface Params {
    wordList: string[]
  }

  export interface Response {
    result: boolean
    count: number // updated count
  }
}

export namespace IUpdateForbidWord {
  export interface Params {
    wordList: Partial<schema_chat_forbid_word>[]
  }

  export interface Response {
    result: boolean
    count: number // updated count
  }
}

export namespace IDeleteForbidWord {
  export interface Params {
    wordList: Partial<schema_chat_forbid_word>[]
  }

  export interface Response {
    result: boolean
    count: number // updated count
  }
}

export namespace IFindChatEmoticon {
  export interface Params {
    version: number
  }
  export interface Response {
    result: Partial<schema_chat_emoticon>[]
  }
}

export namespace IAddChatEmoticon {
  export interface Params extends FormData{
  }
  export interface Response {
    result: Partial<schema_chat_emoticon>[] // 새로 생성된 이모티콘 디비 모델을 반환
    version: number
  }
}

export namespace IUpdateChatEmoticon {
  export interface Params {
    emoticonList: Partial<schema_chat_emoticon>[]
  }
  export interface Response {
    result: Partial<schema_chat_emoticon>[] // update 된 이모티콘 정보 반환
  }
}

export interface ChatStatistics {
  createdAt: Date
  idx: number
  adminCount: number
  userCount: number
  totalCount: number
  chatUserCount: number
  chatConnectUserCount: number
}

export enum ChatStatisticsViewType {
  HOUR = 'hour', DAY = 'day', MONTH = 'month',
}

export namespace IFindChatStatistics {
  export interface Params {
    viewType: ChatStatisticsViewType // hour, day, month
    fromDate: Date
    toDate?: Date
  }
  export interface Response {
    result: ChatStatistics[]
  }
}

export interface UserChatCountInfo {
  userId: string
  userNick: string
  total: number
  normal: number
  emoticon: number
  image: number
  betHistory: number
  adminCount: number
  normalCount: number
  lastCreatedAt: Date
}

export interface UserChatCountInfoResult {
  totalCount: number
  userChatCountInfoList: UserChatCountInfo[]
}

export namespace IFindUserChatCountInfoList {
  export interface Params {
    pageNum: number
    pageLimit: number // default 10
    sortBy: string // table column name
    sortDirection: string, // desc, asc
    fromDate?: Date
    toDate?: Date
  }
  export interface Response {
    result: UserChatCountInfo[]
  }
}

export namespace IGetCheerForTeamMatchList {
  export interface Params {
    cheerForTeamType: CHEER_FOR_TEAM_MATCH_TYPE
    cheerForTeamSubType: CHEER_FOR_TEAM_MATCH_SUB_TYPE
    // 처음 응원글 매치 조회시 서브 타입(진행,예정,종료) 에 해당하는 매치가 없을 경우 다음 서브 타입으로 자동 조회 할지 여부.
    autoSwitchMainType: boolean
    autoSwitchSubType: boolean
    pageNum: number
    limit?: number // 기본값 20
  }

  export interface Response extends Schema$ResponseBase {
    items: Schema$CheerForTeamData[]
    myBetTotalCount: number // 응원글 각 탭에 표시할 매치 개수
    favoriteTotalCount: number // 응원글 각 탭에 표시할 매치 개수
    liveTotalCount: number // 응원글 각 탭에 표시할 매치 개수
    // 응원글 매치 목록 첫 조회시 메인 및 서브타입에 해당하는 데이터가 없으면 다음 메인 및 서브 타입으로 조회 하고
    // 그 결과 최종 선택된 메인 및 서브타입을 클라이언트에 알려주기 위해.
    currentMatchListType: CHEER_FOR_TEAM_MATCH_TYPE
    currentMatchListSubType: CHEER_FOR_TEAM_MATCH_SUB_TYPE
    hasMore: boolean
  }
}

export namespace IChatBlockUserInfos {
  export interface Params {}
  export interface Response extends Schema$ResponseBase{
    userPromiseIds?: string[]
  }
}

export namespace IGetCheerForTeamMatchInfo {
  export interface Params {
    matchId: number
  }

  export interface Response extends Schema$ResponseBase {
    item: Schema$CheerForTeamData
  }
}

export namespace ISharedBettingHistoryList {
  export interface Params {
    offset?: number
    baseMonth?: string
  }

  export interface Response extends Schema$ResponseBase {
    items: Schema$ChatMessage[]
    hasMore?: boolean
  }
}
